import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  FileText,
  Users,
  Gift,
  Inbox,
  Coins,
  Siren,
  LogOut,
  Sparkles,
  HelpCircle, KanbanSquare } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useCredits } from '../../contexts/CreditsContext';
import { Wordmark } from './Wordmark';
import { surfaceConfig, type SurfaceConfig } from '../../lib/surface';

const ALL_TABS = [
  { key: 'home', to: '/', label: 'Home', icon: Home, end: true },
  { key: 'documents', to: '/documents', label: 'Documents', icon: FileText, end: false },
  { key: 'contacts', to: '/contacts', label: 'People', icon: Users, end: false },
  { key: 'pipeline', to: '/pipeline', label: 'Pipeline', icon: KanbanSquare, end: false },
  { key: 'draws', to: '/draws', label: 'Draws', icon: Gift, end: false },
  { key: 'inbox', to: '/inbox', label: 'Inbox', icon: Inbox, end: false },
] as const;

/** The door decides which tabs exist and what the contact list is called. */
const CONFIG: SurfaceConfig = surfaceConfig();
const TABS = ALL_TABS.filter((t) =>
  CONFIG.tabs.includes(t.key as SurfaceConfig['tabs'][number]),
).map((t) => (t.key === 'contacts' ? { ...t, label: CONFIG.contactsLabel } : t));

function tabClasses(isActive: boolean): string {
  return `flex flex-col items-center gap-0.5 rounded-chip px-2 py-1.5 text-[11px] font-semibold transition-colors md:flex-row md:gap-2.5 md:px-3 md:py-2.5 md:text-sm ${
    isActive ? 'text-ink md:bg-ink md:text-paper' : 'text-mist-400 hover:text-ink md:hover:bg-mist-100'
  }`;
}

/**
 * Pages that show a table rather than a form.
 *
 * Everything else keeps its CONTENT at a reading width, because a settings form
 * stretched across a 27-inch monitor is worse than one that stops. These are
 * lists of columns, and columns need room — a table inside a 36rem column is a
 * table that scrolls sideways to show its third field. The frame never changes
 * either way; only the column inside it does.
 */
const WIDE_PATHS = ['/', '/contacts', '/consent', '/draws', '/documents', '/inbox', '/breach', '/settings', '/help', '/adviser', '/pipeline', '/leads', '/financing'];
/** Prefixes, for pages whose path carries an id. */
const WIDE_PREFIXES = ['/consent/forms/', '/contacts/', '/draws/', '/documents/', '/inbox/', '/breach/', '/pipeline/', '/financing/'];
/** The exceptions inside those prefixes that really are forms. */
const NARROW_PATHS = ['/draws/new', '/breach/new'];
/**
 * The board is the one page that is neither a form nor a table: it is a row of
 * stages you scroll sideways through. Stopping it at the table width cut the
 * fourth stage in half while a third of the frame stood empty, so it gets the
 * whole frame — still the frame, so the rail and the wordmark do not move.
 */
const FULL_PATHS = ['/pipeline'];

export function AppShell() {
  const { organisation, signOut } = useAuth();
  const { balance, loading: creditsLoading } = useCredits();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [helpOpen, setHelpOpen] = useState(false);
  const helpRef = useRef<HTMLDivElement>(null);

  // The help menu closes like any menu: click elsewhere, or go somewhere.
  useEffect(() => {
    if (!helpOpen) return;
    const onDown = (e: MouseEvent) => {
      if (helpRef.current && !helpRef.current.contains(e.target as Node)) setHelpOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [helpOpen]);
  useEffect(() => {
    setHelpOpen(false);
  }, [pathname]);

  const wide =
    !NARROW_PATHS.includes(pathname) &&
    (WIDE_PATHS.includes(pathname) || WIDE_PREFIXES.some((p) => pathname.startsWith(p)));
  const full = FULL_PATHS.includes(pathname);

  return (
    <div className="min-h-screen bg-paper">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-mist-200 bg-paper/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-2.5">
          <Link to="/" className="min-w-0" aria-label="Home">
            <Wordmark className="text-lg" />
            {organisation && (
              <p className="truncate text-xs text-mist-500">{organisation.name}</p>
            )}
          </Link>
          <div className="flex items-center gap-2">
            {/* Asking a question is not a place you navigate to — it is
                something you do in the middle of whatever else you were doing,
                so it lives in the chrome rather than on one page. */}
            <NavLink
              to="/adviser"
              className={({ isActive }) =>
                `inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${
                  isActive
                    ? 'bg-ink text-white'
                    : 'border border-mist-200 bg-white text-ink hover:border-mist-300'
                }`
              }
            >
              <Sparkles className="h-4 w-4 text-gold" />
              <span className="hidden sm:inline">Ask about PDPO</span>
              <span className="sr-only sm:hidden">Ask about PDPO</span>
            </NavLink>
            {/* Help: the guide, and the adviser that has read it. */}
            <div className="relative" ref={helpRef}>
              <button
                type="button"
                onClick={() => setHelpOpen((o) => !o)}
                aria-label="Help"
                aria-expanded={helpOpen}
                title="Help"
                className="rounded-full p-2 text-mist-400 hover:bg-mist-100 hover:text-ink"
              >
                <HelpCircle className="h-4 w-4" />
              </button>
              {helpOpen && (
                <div className="fixed inset-x-4 top-16 z-50 overflow-hidden rounded-card border border-mist-200 bg-white shadow-float sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-1.5 sm:w-64">
                  <Link
                    to="/help"
                    onClick={() => {
                      setHelpOpen(false);
                      if (pathname === '/help') window.scrollTo({ top: 0 });
                    }}
                    className="block px-4 py-2.5 text-sm font-semibold text-ink hover:bg-mist-50"
                  >
                    How everything works
                    <span className="block text-xs font-normal text-mist-500">
                      The user guide, screen by screen
                    </span>
                  </Link>
                  <Link
                    to="/help#faq"
                    onClick={() => {
                      setHelpOpen(false);
                      if (pathname === '/help') {
                        document.getElementById('faq')?.scrollIntoView({ block: 'start' });
                      }
                    }}
                    className="block border-t border-mist-100 px-4 py-2.5 text-sm font-semibold text-ink hover:bg-mist-50"
                  >
                    Questions people ask
                  </Link>
                  <Link
                    to="/adviser"
                    onClick={() => setHelpOpen(false)}
                    className="flex items-center gap-2 border-t border-mist-100 px-4 py-2.5 text-sm font-semibold text-ink hover:bg-mist-50"
                  >
                    <Sparkles className="h-3.5 w-3.5 text-gold" />
                    Ask the adviser
                    <span className="ml-auto text-xs font-normal text-mist-500">1 credit</span>
                  </Link>
                </div>
              )}
            </div>
            {/* The balance is a door, not a badge: it opens Settings, where
                the credits card shows activity and sells more. */}
            <NavLink
              to="/settings"
              title="Credits — open Settings"
              className="inline-flex items-center gap-1.5 rounded-full bg-gold-tint px-3 py-1 font-mono text-sm font-semibold text-ink transition-colors hover:bg-gold/30"
            >
              <Coins className="h-4 w-4 text-gold" />
              {creditsLoading ? '…' : balance}
            </NavLink>
            <button
              type="button"
              onClick={() => {
                signOut().catch(() => undefined);
              }}
              aria-label="Sign out"
              title="Sign out"
              className="rounded-full p-2 text-mist-400 hover:bg-mist-100 hover:text-ink"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      {/* The frame is constant — max-w-7xl on every page — so the wordmark and
          the nav rail never move as you navigate. What varies per page is only
          the CONTENT column inside it: tables get the full frame, forms stop at
          a reading width. Changing the frame itself made the whole app jump
          sideways between Home and People. */}
      <div className="mx-auto flex max-w-7xl md:gap-6 md:px-4">
        {/* Desktop left rail */}
        <nav className="sticky top-[57px] hidden h-[calc(100vh-57px)] w-48 flex-shrink-0 flex-col gap-1 py-6 md:flex">
          {TABS.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} className={({ isActive }) => tabClasses(isActive)}>
              <Icon className="h-5 w-5" />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Page content. The frame never moves; inside it, every page is a
            CENTRED column in the space beside the rail — two sizes of the same
            shape. Forms stop at a reading width; tables get a wider column,
            but not the whole frame: a full-bleed table beside centred forms
            read as a different app. */}
        <main className="w-full min-w-0 flex-1 px-4 pb-28 pt-5 md:px-0 md:pb-12">
          <div className={full ? '' : wide ? 'md:mx-auto md:max-w-4xl' : 'md:mx-auto md:max-w-xl'}>
            <Outlet />
          </div>
        </main>
      </div>

      {/* Floating breach button */}
      <button
        type="button"
        onClick={() => navigate('/breach/new')}
        aria-label="Something went wrong? Report a data incident"
        title="Something went wrong?"
        // Sits above the tab bar by default. A page with its own pinned bottom
        // bar raises it by setting --go-fab-bottom, rather than the shell
        // knowing which routes those are — see Adviser.
        className="fixed bottom-[var(--go-fab-bottom,5rem)] right-4 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-clay text-white shadow-float transition-transform hover:scale-105 active:scale-95 md:bottom-6 md:right-6"
      >
        <Siren className="h-5 w-5" />
      </button>

      {/* Mobile bottom tab bar */}
      <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-mist-200 bg-white pb-[env(safe-area-inset-bottom)] md:hidden">
        <div className="mx-auto flex max-w-md items-stretch justify-around px-1 py-1.5">
          {TABS.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} className={({ isActive }) => tabClasses(isActive)}>
              <Icon className="h-5 w-5" />
              {label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
